import type { RuntimeWidgetConfig } from "../../core/config/public-config.types.ts";
import {
  normalizeFeatureRuntimeError,
  type FeatureRuntimePhase,
  type NormalizedFeatureRuntimeError,
} from "./feature-runtime-errors.ts";
import {
  runtimeFeatureRegistry,
  type RuntimeFeatureContext,
  type RuntimeFeatureModule,
} from "./runtime-feature-registry.ts";
import type { RenderFeatureControlInput } from "./feature-panel.ts";

export type FeatureRuntime = {
  applyAll: (nextPreferences?: Record<string, unknown>) => void;
  applyFeature: (key: string, nextPreference: unknown) => void;
  getErrors: () => NormalizedFeatureRuntimeError[];
  getRenderableFeatureKeys: () => Set<string>;
  renderControl: (input: RenderFeatureControlInput) => HTMLElement;
  resetAll: () => void;
};

export type CreateFeatureRuntimeInput = {
  config: RuntimeWidgetConfig;
  document?: Document;
  preferences?: Record<string, unknown>;
  registry?: RuntimeFeatureModule[];
};

function isEnabledRuntimeFeature(
  feature: RuntimeWidgetConfig["features"][string] | undefined,
): feature is RuntimeWidgetConfig["features"][string] {
  return feature?.enabled === true && "config" in feature;
}

export function createFeatureRuntime({
  config,
  document = globalThis.document,
  preferences: initialPreferences = {},
  registry = runtimeFeatureRegistry,
}: CreateFeatureRuntimeInput): FeatureRuntime {
  let preferences = initialPreferences;
  const errors: NormalizedFeatureRuntimeError[] = [];
  const sessionDisabledFeatureKeys = new Set<string>();
  const appliedFeatureKeys = new Set<string>();
  const supportCache = new Map<string, boolean>();

  const recordError = (
    module: RuntimeFeatureModule,
    phase: FeatureRuntimePhase,
    error: unknown,
  ) => {
    errors.push(
      normalizeFeatureRuntimeError({
        error,
        featureKey: module.key,
        phase,
      }),
    );
  };

  const createContext = (module: RuntimeFeatureModule): RuntimeFeatureContext | null => {
    const featureConfig = config.features[module.key];
    if (!isEnabledRuntimeFeature(featureConfig)) {
      return null;
    }

    return {
      config: featureConfig.config,
      definition: module.definition,
      document,
      featureConfig,
      key: module.key,
      preference: preferences[module.key],
    };
  };

  const checkSupport = (module: RuntimeFeatureModule, context: RuntimeFeatureContext) => {
    if (sessionDisabledFeatureKeys.has(module.key)) {
      return false;
    }

    if (supportCache.has(module.key)) {
      return supportCache.get(module.key) === true;
    }

    if (!module.isSupported) {
      supportCache.set(module.key, true);
      return true;
    }

    try {
      const supported = module.isSupported(context) !== false;
      supportCache.set(module.key, supported);
      return supported;
    } catch (error) {
      recordError(module, "support", error);
      sessionDisabledFeatureKeys.add(module.key);
      supportCache.set(module.key, false);
      return false;
    }
  };

  const getRunnableModules = () => {
    return registry
      .map((module) => {
        const context = createContext(module);
        return context ? { module, context } : null;
      })
      .filter(
        (
          entry,
        ): entry is {
          context: RuntimeFeatureContext;
          module: RuntimeFeatureModule;
        } => entry !== null,
      );
  };

  const applyModule = (module: RuntimeFeatureModule, context: RuntimeFeatureContext) => {
    if (!checkSupport(module, context)) {
      return;
    }

    try {
      module.apply(context);
      appliedFeatureKeys.add(module.key);
    } catch (error) {
      recordError(module, "apply", error);
      sessionDisabledFeatureKeys.add(module.key);
    }
  };

  return {
    applyAll(nextPreferences) {
      if (nextPreferences) {
        preferences = nextPreferences;
      }

      for (const { module, context } of getRunnableModules()) {
        applyModule(module, context);
      }
    },
    applyFeature(key, nextPreference) {
      preferences = {
        ...preferences,
        [key]: nextPreference,
      };

      const entry = getRunnableModules().find(({ module }) => module.key === key);
      if (!entry || sessionDisabledFeatureKeys.has(key)) {
        return;
      }

      applyModule(entry.module, {
        ...entry.context,
        preference: nextPreference,
      });
    },
    getErrors() {
      return [...errors];
    },
    getRenderableFeatureKeys() {
      const keys = new Set<string>();
      for (const { module, context } of getRunnableModules()) {
        if (checkSupport(module, context)) {
          keys.add(module.key);
        }
      }
      return keys;
    },
    renderControl(input) {
      const entry = getRunnableModules().find(({ module }) => module.key === input.key);
      if (!entry || sessionDisabledFeatureKeys.has(input.key)) {
        throw new Error("Feature unavailable");
      }

      if (!checkSupport(entry.module, entry.context)) {
        throw new Error("Feature unsupported");
      }

      try {
        return entry.module.renderControl(input);
      } catch (error) {
        recordError(entry.module, "render", error);
        sessionDisabledFeatureKeys.add(entry.module.key);
        throw error;
      }
    },
    resetAll() {
      for (const { module, context } of getRunnableModules()) {
        if (!appliedFeatureKeys.has(module.key)) {
          continue;
        }

        try {
          module.reset(context);
        } catch (error) {
          recordError(module, "reset", error);
        }
      }

      appliedFeatureKeys.clear();
    },
  };
}
